import PageMeta from '@/components/PageMeta';
import SectionHeader from '@/components/SectionHeader';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ENQUIRY_TYPES = [
  'Physiotherapists',
  'Allied-health professionals',
  'Hospitals and clinics',
  'Rehabilitation organisations',
  'Support organisations',
  'Researchers',
  'Movement professionals',
];

const FUTURE = [
  'Professional dashboards',
  'Clinician access to user history',
  'Progress oversight',
  'Exercise assignment',
  'Recommendations',
  'Clinical workflow integrations',
  'Patient-monitoring functions',
];

export default function ForProfessionals() {
  return (
    <div>
      <PageMeta
        title="For professionals"
        description="Exploring a better bridge between professional guidance and everyday practice."
      />
      <section className="vl-container py-16 lg:py-24">
        <p className="mono-label text-vl-violet">For professionals</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink max-w-3xl">
          Exploring a better bridge between guidance and everyday practice.
        </h1>
        <p className="mt-6 text-lg text-vl-ink/70 max-w-2xl leading-relaxed">
          Valanti Labs is being developed as a supportive layer that may help users follow movement guidance with more
          structure and clarity between appointments. It is not designed to replace assessment, clinical reasoning,
          diagnosis, treatment, or professional supervision.
        </p>
      </section>

      <section className="bg-vl-bg2 border-y hairline">
        <div className="vl-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <SectionHeader
              kicker="Open to enquiries from"
              title="Allied health, hospitals, clinics, researchers and movement professionals."
              lead="If your organisation works with movement, rehabilitation, recovery or strength, we would value an honest conversation."
            />
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-8 rounded-full bg-vl-ink text-vl-bg px-6 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors"
            >
              Get in touch <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl bg-vl-surface border hairline p-8">
              <p className="mono-label text-vl-violet mb-4">We're listening to</p>
              <ul className="space-y-2 text-vl-ink/80">
                {ENQUIRY_TYPES.map((e) => (
                  <li key={e} className="flex items-start gap-3">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-vl-violet mt-2.5" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="vl-container py-24 lg:py-32">
        <SectionHeader
          kicker="Future directions — not currently available"
          title="What we're researching for professional contexts."
          lead="These directions are being explored. They are not present in the current release and have not been validated for clinical use."
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {FUTURE.map((f) => (
            <div key={f} className="rounded-2xl border hairline bg-vl-surface p-5">
              <span className="mono-label text-vl-violet">Future direction</span>
              <p className="mt-2 text-vl-ink leading-snug">{f}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-sm text-vl-ink/60 max-w-2xl">
          Valanti Labs does not display professional or institutional logos without written permission. If your
          organisation is interested in a future pilot or research collaboration, please get in touch.
        </p>
      </section>
    </div>
  );
}
